/**
 * Share utility functions for sharing game content
 */

import { isMobile } from "mobile-device-detect";
import { reportConversionEvent } from "./Analytics/AnalyticsConversionEvents";
import { EVENT_NAMES } from "./Analytics/AnalyticsConstants";

export interface ShareOptions {
  /** The text content to share */
  text: string;
  /** Optional title for the share (defaults to first line of text) */
  title?: string;
  /** Whether to report the share as a conversion event (default: true) */
  reportAnalytics?: boolean;
  /** Custom analytics message */
  analyticsMessage?: string;
  /** Callback when share is successful */
  onSuccess?: () => void;
  /** Callback when share fails */
  onError?: (error: any) => void;
}

/**
 * Share content using the Web Share API on mobile or clipboard on desktop
 * @param options - Share configuration options
 * @returns Promise that resolves when share is complete
 */
export const shareContent = async (options: ShareOptions): Promise<void> => {
  const {
    text,
    title,
    reportAnalytics = true,
    analyticsMessage = "User shared content",
    onSuccess,
    onError
  } = options;

  const shareTitle = title || text.split("\n")[0];

  // Report analytics if enabled
  if (reportAnalytics) {
    reportConversionEvent(EVENT_NAMES.BUTTON_CLICK_SHARE, {}, analyticsMessage);
  }

  try {
    if (isMobile && navigator.share) {
      // Use Web Share API on mobile
      await navigator.share({
        title: shareTitle,
        text: text,
      });
      onSuccess?.();
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      // Use clipboard API on desktop
      await navigator.clipboard.writeText(text);
      onSuccess?.();
    } else {
      throw new Error("No sharing method available");
    }
  } catch (error) {
    console.error("Error while sharing:", error);
    onError?.(error);
    throw error;
  }
};
